import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Square, Terminal as TerminalIcon, ChevronDown } from 'lucide-react';
import axios from 'axios';

const authHeaders = () => {
  const token = localStorage.getItem('authToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Vite/npm Farbcodes aus Terminal-Logs entfernen */
const stripAnsi = (input) =>
  String(input || '')
    .replace(/\u001b\[[0-9;?]*[ -/]*[@-~]/g, '')
    .replace(/\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)/g, '')
    .replace(/\u001b[@-Z\\-_]/g, '')
    .replace(/\u001b/g, '');

/**
 * Live-Ausgabe von npm install / npm run dev / run_check im Agent-Projekt.
 */
const AgentTerminal = ({ projectId, refreshKey = 0, active = true, onClose }) => {
  const preRef = useRef(null);
  const stickBottomRef = useRef(true);
  const [log, setLog] = useState('');
  const [command, setCommand] = useState('');
  const [running, setRunning] = useState(false);
  const [kind, setKind] = useState('');
  const [port, setPort] = useState(null);
  const [exitCode, setExitCode] = useState(null);
  const [error, setError] = useState('');
  const [stopping, setStopping] = useState(false);

  const load = async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`/api/ai/projects/${encodeURIComponent(projectId)}/terminal`, {
        headers: authHeaders(),
      });
      setLog(stripAnsi(res.data?.log || ''));
      setCommand(String(res.data?.command || ''));
      setRunning(!!res.data?.running);
      setKind(String(res.data?.kind || ''));
      setPort(res.data?.port != null ? Number(res.data.port) : null);
      setExitCode(res.data?.exitCode != null ? Number(res.data.exitCode) : null);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Terminal nicht erreichbar');
    }
  };

  useEffect(() => {
    if (!active) return undefined;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshKey, active]);

  useEffect(() => {
    if (!projectId || !active) return undefined;
    const id = window.setInterval(load, running ? 700 : 2000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, running, active]);

  useEffect(() => {
    if (!stickBottomRef.current || !preRef.current) return;
    preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [log]);

  const onScroll = () => {
    const el = preRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickBottomRef.current = dist < 40;
  };

  const stopPreview = async () => {
    if (!projectId || stopping) return;
    setStopping(true);
    try {
      await axios.post(
        `/api/ai/projects/${encodeURIComponent(projectId)}/preview/stop`,
        {},
        { headers: authHeaders() }
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Stop fehlgeschlagen');
    } finally {
      setStopping(false);
    }
  };

  if (!projectId) {
    return (
      <div className="h-full flex items-center justify-center text-[12px] text-neutral-400 bg-neutral-950">
        Projekt wählen für Terminal
      </div>
    );
  }

  const statusLabel = running
    ? kind === 'preview'
      ? 'Preview läuft'
      : kind === 'install'
        ? 'Install läuft'
        : 'Prozess läuft'
    : exitCode === 0
      ? 'Beendet (0)'
      : exitCode != null
        ? `Beendet (${exitCode})`
        : 'Bereit';

  return (
    <div className="h-full min-h-0 flex flex-col bg-neutral-950 text-neutral-200">
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1 border-b border-neutral-800 bg-neutral-900/80">
        <TerminalIcon className="w-3.5 h-3.5 text-emerald-400/90" />
        <span className="text-[11px] font-medium text-neutral-300 truncate flex-1 min-w-0">
          {command || 'Kein aktiver Befehl'}
          {port ? <span className="text-neutral-500"> · :{port}</span> : null}
        </span>
        <span
          className={`inline-flex items-center gap-1 text-[10px] tabular-nums ${
            running ? 'text-emerald-400' : 'text-neutral-500'
          }`}
        >
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {statusLabel}
        </span>
        {kind === 'preview' && running ? (
          <button
            type="button"
            onClick={stopPreview}
            disabled={stopping}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
            title="Preview stoppen"
          >
            <Square className="w-3 h-3" />
            Stop
          </button>
        ) : null}
        {typeof onClose === 'function' ? (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-md p-0.5 text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800"
            title="Terminal ausblenden"
            aria-label="Terminal ausblenden"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>
      {error ? (
        <div className="flex-shrink-0 px-3 py-1 text-[11px] text-red-400 border-b border-neutral-800">
          {error}
        </div>
      ) : null}
      <pre
        ref={preRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-auto p-3 text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words text-neutral-300 selection:bg-emerald-500/30"
      >
        {log.trim()
          ? log.replace(/^\n+/, '')
          : 'Noch keine Ausgabe.\nStarte Preview oder einen Check — Logs erscheinen hier live.'}
      </pre>
    </div>
  );
};

export default AgentTerminal;
